/**
 * Tela de entrada: login e cadastro.
 *
 * Responsabilidade: coletar e-mail/senha, chamar `api.login`/`api.registrar` e
 * avisar quem chamou quando houver sessao.
 *
 * NAO e responsabilidade deste arquivo: guardar token (`api.ts`), montar
 * estado (`store.ts`) nem decidir para onde ir depois (`main.ts`).
 *
 * O cadastro cria tenant + usuario `owner` numa chamada so (C2), entao o campo
 * "nome do cultivo" so aparece nesse modo — e o nome do TENANT, nao do
 * aparelho: o aparelho ganha nome no painel local, e ele chega pelo digital
 * twin (`dev_name`, contrato §5).
 */

import * as api from "./../api";
import { ErroApi, SemRede } from "./../api";
import { el } from "./../ui";

/** `app/auth/passwords.py::MIN_PASSWORD_LEN` */
const MIN_SENHA = 10;

export function montar(aoEntrar: () => void): HTMLElement {
  let modo: "login" | "registro" = "login";

  const email = el("input", {
    type: "email",
    id: "email",
    autocomplete: "email",
    required: true,
    // "seu@email.com" e nao "voce@exemplo.com": e o unico texto da tela que o
    // usuario poderia DIGITAR, e endereco de e-mail com acento nao se digita.
    placeholder: "seu@email.com",
  });
  const senha = el("input", {
    type: "password",
    id: "senha",
    autocomplete: "current-password",
    required: true,
    minlength: MIN_SENHA,
  });
  const tenant = el("input", {
    type: "text",
    id: "tenant",
    autocomplete: "organization",
    placeholder: "Meu cultivo",
  });
  const campoTenant = el(
    "label",
    { class: "campo", hidden: true },
    el("span", { text: "Nome do cultivo" }),
    tenant,
  );

  const erro = el("p", { class: "erro", role: "alert", hidden: true });
  const botao = el("button", { type: "submit", class: "primary", text: "Entrar" });
  const alternar = el("button", {
    type: "button",
    class: "link",
    text: "Não tenho conta",
  });

  function trocarModo(): void {
    modo = modo === "login" ? "registro" : "login";
    const registrando = modo === "registro";
    campoTenant.hidden = !registrando;
    tenant.required = registrando;
    senha.setAttribute("autocomplete", registrando ? "new-password" : "current-password");
    botao.textContent = registrando ? "Criar conta" : "Entrar";
    alternar.textContent = registrando ? "Já tenho conta" : "Não tenho conta";
    erro.hidden = true;
  }
  alternar.addEventListener("click", trocarModo);

  const formulario = el(
    "form",
    { class: "login-form", novalidate: true },
    el("label", { class: "campo" }, el("span", { text: "E-mail" }), email),
    el("label", { class: "campo" }, el("span", { text: "Senha" }), senha),
    campoTenant,
    erro,
    botao,
    alternar,
  );

  formulario.addEventListener("submit", (evento) => {
    evento.preventDefault();
    void enviar();
  });

  async function enviar(): Promise<void> {
    erro.hidden = true;
    if (modo === "registro" && senha.value.length < MIN_SENHA) {
      return falhar(`A senha precisa de pelo menos ${MIN_SENHA} caracteres.`);
    }
    botao.disabled = true;
    botao.textContent = "…";
    try {
      if (modo === "login") await api.login(email.value.trim(), senha.value);
      else await api.registrar(email.value.trim(), senha.value, tenant.value.trim());
      aoEntrar();
    } catch (excecao) {
      falhar(mensagem(excecao));
    } finally {
      botao.disabled = false;
      botao.textContent = modo === "registro" ? "Criar conta" : "Entrar";
    }
  }

  function falhar(texto: string): void {
    erro.textContent = texto;
    erro.hidden = false;
  }

  return el(
    "main",
    { class: "login" },
    el(
      "header",
      { class: "login-marca" },
      el("span", { class: "leaf", text: "🌱" }),
      el("h1", { text: "GrowController" }),
      el("p", { class: "hint", text: "Painel remoto" }),
    ),
    formulario,
    el("p", {
      class: "hint rodape",
      text:
        "Este painel é adicional. O controlador continua funcionando sozinho, " +
        "com o painel local em grow.local, mesmo com esta página fora do ar.",
    }),
  );
}

function mensagem(excecao: unknown): string {
  if (excecao instanceof SemRede) {
    return "Sem conexão com a nuvem. O painel local em grow.local não depende dela.";
  }
  if (excecao instanceof ErroApi) {
    if (excecao.status === 429) return "Muitas tentativas. Espere um minuto.";
    return excecao.detalhe;
  }
  // `SemSessao` cai aqui: 401 no login e credencial errada, nao sessao vencida.
  if (excecao instanceof Error && excecao.message !== "") return excecao.message;
  return "Não foi possível entrar.";
}
